import * as React from "react";
import {
    Box,
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Button,
} from "@mui/material";
import { LOCAL_CONSTANT } from "../Const";
import {
    tableStyle,
    tableHeaderStyle,
    tableCellStyle,
    tableHeaderSmallStyle,
    tableCellSmallStyle,
    tableHeaderDateStyle,
    tableCellDateStyle,
    tableCellWhiteSpace,
    tableHeaderNameStyle,
    tableCellNameStyle,
} from "../components/ChangeHistoryTableStyle";
import { apiRequest } from "../../api/ApiCall";
import { useUtilityContext } from "../../utility-provider";
import ChangeHistoryTableComment from "../components/ChangeHistoryTableComment";
import FilterModal from "../components/FilterModal";
import { FilterCriteria, initFilterCriteriaObj, FilterOptions, initFilterOptionsObj, ChangeHistoryItem, initChangeHistoryList } from "../../data/type";
import dayjs from "dayjs";
import {
    changeHistoryTableName,
    changeHistoryColumnName,
    changeHistorySelecterDisplay,
    addSortData,
    generateFilterOptions,
    applyFilter,
    groupChangeHistoryByItemHierarchy,
    getItemGroupSummary,
    processCommentsForCombination,
} from "../components/ChangeHistoryFunc";
import { useUserContext } from "../../user-provider";
import { decryptQueryParam } from '../../utility/util';
import { usePlanAreaSearchByIdForHeader } from "../Common";

export interface ItemChangeHistoryProps {
    props: {
        width: number;
        height: number;
    };
}


const ItemChangeHistory: React.FC<ItemChangeHistoryProps> = ({ props }) => {
    const utilityCtx = useUtilityContext();
    //URLパラメータ
    const [planAreaId, setPlanAreaId] = React.useState("");
    const [branch, setBranch] = React.useState("");
    // 最新のブランチ
    const [latestBranch, setLatestBranch] = React.useState<string>("");

    const [changeHistory, setChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList);
    const [comment, setComment] = React.useState<any[]>([]);
    const [tableNameMaster, setTableNameMaster] = React.useState<any>([]);
    const [commentOpen, setCommentOpen] = React.useState<boolean>(false);
    const [codeMaster, setCodeMaster] = React.useState<any>();
    const [userType, setUserType] = React.useState<string>("");
    
    // フィルタ関連の状態管理
    const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false); // フィルタモーダルの開閉状態
    const [originalChangeHistory, setOriginalChangeHistory] = React.useState<ChangeHistoryItem[]>(initChangeHistoryList); // フィルタ前の元データ
    const [filterCriteria, setFilterCriteria] = React.useState<FilterCriteria>(initFilterCriteriaObj);  // 現在のフィルタ条件
    const [filterOptions, setFilterOptions] = React.useState<FilterOptions>(initFilterOptionsObj); // フィルタ選択肢
    
    // ページグループ化関連の状態管理
    const [expandedBranches, setExpandedBranches] = React.useState<{[key: string]: boolean}>({}); // 枝グループの展開状態
    const [expandedTables, setExpandedTables] = React.useState<{[key: string]: boolean}>({}); // テーブル論理名グループの展開状態（商圏ページ用）
    const [expandedItems, setExpandedItems] = React.useState<{[key: string]: boolean}>({}); // 項目グループの展開状態
    const [isAllExpanded, setIsAllExpanded] = React.useState<boolean>(false); // 全展開状態の管理
    
    const user = useUserContext();
    const updateHeader = usePlanAreaSearchByIdForHeader();


    React.useEffect(() => {
        //暗号化されたクエリパラメータを復号化
        const search = window.location.search;
        const urlParam = search.substring(1);
        decryptQueryParam(urlParam).then((res) => {
            setPlanAreaId(res[0]);
            setBranch(res[1]);
        }).catch((e:any) => {
            console.log(e);
            if (utilityCtx.hideSpinner) {
                utilityCtx.hideSpinner();
            }
        });
        const tableNameMasterObj = localStorage.getItem("tableNameMaster");
        if (tableNameMasterObj) {
            setTableNameMaster(JSON.parse(tableNameMasterObj));
        }
        const res: any = localStorage.getItem("codeMaster");
        if (res) {
            const d = JSON.parse(res);
            setCodeMaster(d);
        }
    }, []);

    React.useEffect(()=>{
        if(planAreaId !== ""){
            callLatestBranch();
            callChangeHistory();
            // 候補地情報をRedux storeに設定（共有関数を使用）
            if (user.userData) {
                updateHeader(planAreaId, user.userData);
            }
        }
    },[planAreaId, user.userData]);

    const callLatestBranch = async () => {
        const params = {
            mode: "regist",
            endPoint: "/location/Reception/v1/latestBranch",
            query: {
                plan_area_id: planAreaId,
            },
        };
        try {
            const res = await apiRequest(params);
            if (res.data && res.data.dataList && res.data.dataList.length > 0) {
                try {
                    const branchData = JSON.parse(res.data.dataList[0]);
                    if (branchData.latest_branch) {
                        setLatestBranch(branchData.latest_branch);
                    }
                } catch (parseError) {
                    console.log("Latest branch JSON parse error:", parseError);
                }
            }
        } catch (e) {
            console.log("Latest branch API error:", e);
        }
    };

    const callChangeHistory = () => {
        // 変更履歴
        const params = {
            mode: "regist",
            endPoint: "/location/system/v1/get-log",
            query: {
                plan_area_id: planAreaId,
                log_type: "rfc-rr",
            },
        };
        // コメント
        const commentParams = {
            mode: "regist",
            endPoint: "/location/system/v1/get-rfc-rr-comment",
            query: {
                plan_area_id: planAreaId,
            },
        };
        if (utilityCtx.showSpinner) {
            utilityCtx.showSpinner();
        }
        (async () => {
            try {
                const res = await apiRequest(params);
                // 変更履歴の表示用に事前計算して追加
                const preCalculatedData = res.data
                    .filter((item: any) => item.target_table && !item.target_table.includes("RR内部用")) // RR内部用ページは変更履歴に表示しない
                    .map((item: any) => ({
                        ...item,
                        tableLogicName: changeHistoryTableName(tableNameMaster, item.target_table, item),
                        columnLogicName: changeHistoryColumnName(tableNameMaster, item.target_column, item),
                        beforeValueDisplay: changeHistorySelecterDisplay(codeMaster, item.target_column, item.target_before_value),
                        afterValueDisplay: changeHistorySelecterDisplay(codeMaster, item.target_column, item.target_after_value)
                    }));
                // ページグループ化とソートのためのpage_sort値とcolumn_sort値を付与（事前計算されたデータを使用）
                const enhancedData = addSortData(preCalculatedData, codeMaster, tableNameMaster);
                
                // コメントデータを取得して処理
                const comment_res = await apiRequest(commentParams);
                setComment(comment_res.data);
                
                // コメントデータを変更履歴と同じ構造に変換
                const processedComments = processCommentsForCombination(comment_res.data, codeMaster);
                
                // 変更履歴とコメントデータを結合
                const combinedData = [...enhancedData, ...processedComments];
                
                setOriginalChangeHistory(combinedData);
                setChangeHistory(combinedData);
                const filterOpts = generateFilterOptions(combinedData, codeMaster);
                setFilterOptions(filterOpts);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            } catch (e) {
                console.log(e);
                if (utilityCtx.hideSpinner) {
                    utilityCtx.hideSpinner();
                }
            }
        })();
    };

    const change_details = (log_type: string) => {
        const change_details_list = [
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ITEM_CHANGE, // "0"
            "",
            "",
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_CVS_CHANGE, // "3"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CONFLICT_SMDS_CHANGE, // "4"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_INTERSECTION_CHANGE, // "5"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.SALES_FORECAST_SUMMARY_TABLE_CHANGE, // "6"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.ADDITIONAL_SURVEY_CHANGE, // "7"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.OTHER_DOCUMENT_CHANGE, // "8"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.RR_INTERIOR_CHANGE, // "9"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.CURRENT_STATUS_PHOTO_CHANGE, // "10"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRAFFIC_VOLUME_CHANGE, // "11"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.FIVEM_EXTRA_CALCULATION_BASIS_CHANGE, // "12"
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.TRADE_AREA_CUT_CHANGE, // 13
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.IMPACT_ASSESSMENT_CHANGE, // 14
            LOCAL_CONSTANT.SYSTEM_LOG_TYPE.MAP_CHANGE, // 15
        ];

        return change_details_list[Number(log_type)];
    };

    const handleCommentOpen = () => {
        setCommentOpen(true);
    };

    const handleSaveComment = () => {
        setCommentOpen(false);
        callChangeHistory();
    };

    const handleCanCelComment = () => {
        setCommentOpen(false);
    };

    const displayTime = (date: string) => {
        const time = new Date(date);
        time.setHours(time.getHours() + 9);
        return dayjs(time).format("YYYY-MM-DD HH:mm:ss");
    };


    // フィルタを適用
    const handleApplyFilter = (filters: FilterCriteria) => {
        const filteredData = applyFilter(originalChangeHistory, filters);
        setChangeHistory(filteredData);
        setFilterCriteria(filters);
    };

    // フィルタモーダルを開く
    const handleFilterOpen = () => {
        setFilterModalOpen(true);
    };

    // フィルタモーダルを閉じる
    const handleFilterClose = () => {
        setFilterModalOpen(false);
    };



    // 枝グループの展開/折りたたみ
    const handleBranchGroupClick = (pageName: string, branchKey: string) => {
        const key = `${pageName}_${branchKey}`;
        setExpandedBranches(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // テーブルグループの展開/折りたたみ（商圏・通行量・競合店・影響度ページ用の4階層構造で特別な処理を含む）
    // 
    // 影響度ページでの使用例：
    // - pageName: "影響度"
    // - branchKey: "枝1"
    // - tableLogicName: "影響度\n足立一ツ家３丁目" (location_impact基本項目の場合)
    //                  "影響度\n足立一ツ家３丁目\n車ルート1\n交差点名" (location_impact_counter項目の場合)
    // 
    // 影響度ページでは店舗名＋ルート＋交差点の組み合わせが第3階層のキーとして使用される
    const handleTableExpandGroupClick = (pageName: string, branchKey: string, tableLogicName: string) => {
        const key = `${pageName}_${branchKey}_${tableLogicName}`;
        setExpandedTables(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // 項目グループの展開/折りたたみ
    // 
    // 影響度ページでの処理：
    // - 4階層構造なのでtableLogicNameパラメータを使用
    // - itemName: "通行台数" など個別項目名
    // - tableLogicName: "影響度\n店舗名" (location_impact基本項目の場合)
    //                  "影響度\n店舗名\nルート名\n交差点名" (location_impact_counter項目の場合)
    // 
    // キー生成例：
    // "影響度_枝1_影響度\n足立一ツ家３丁目_通行台数" (基本項目)
    // "影響度_枝1_影響度\n足立一ツ家３丁目\n車ルート1\n交差点名_通行台数" (カウンター項目)
    const handleItemGroupClick = (pageName: string, branchKey: string, itemName: string, tableLogicName?: string) => {
        const key = tableLogicName 
            ? `${pageName}_${branchKey}_${tableLogicName}_${itemName}` // 4階層ページ（商圏・通行量・競合店・影響度）の場合
            : `${pageName}_${branchKey}_${itemName}`; // 3階層ページ（通常）の場合
        setExpandedItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // 全展開/全折りたたみのトグル機能
    const handleToggleExpandAll = () => {
        if (Object.keys(groupedData).length === 0) return;

        if (isAllExpanded) {
            // 全折りたたみ
            setExpandedBranches({});
            setExpandedTables({});
            setExpandedItems({});
            setIsAllExpanded(false);
        } else {
            // 全展開
            const newExpandedBranches: {[key: string]: boolean} = {};
            const newExpandedTables: {[key: string]: boolean} = {};
            
            Object.entries(groupedData).forEach(([pageName, branches]) => {
                Object.keys(branches as any).forEach(branchKey => {
                    // 第2階層（枝レベル）を展開
                    const branchExpandKey = `${pageName}_${branchKey}`;
                    newExpandedBranches[branchExpandKey] = true;
                    
                    // 4階層ページ（通行量、商圏、競合店、影響度）の場合は第3階層（テーブル論理名レベル）も展開
                    if (pageName === "商圏" || pageName === "通行量" || 
                        pageName === "競合店（CVS）" || pageName === "競合店（SMDS）" || 
                        pageName === "影響度") {
                        const branchContent = (branches as any)[branchKey];
                        if (branchContent && typeof branchContent === "object") {
                            Object.keys(branchContent).forEach(tableLogicName => {
                                const tableExpandKey = `${pageName}_${branchKey}_${tableLogicName}`;
                                newExpandedTables[tableExpandKey] = true;
                            });
                        }
                    }
                });
            });
            
            setExpandedBranches(newExpandedBranches);
            setExpandedTables(newExpandedTables);
            setIsAllExpanded(true);
        }
    };

    //ユーザー取得
    React.useEffect(() => {
        if (JSON.parse(user.userData || "null") !== null) {
            setUserType(JSON.parse(user.userData || "null").user_type);
        }
    }, [user]);

    // React.useMemoを使用してデータを階層構造でグループ化
    // groupChangeHistoryByItemHierarchy関数による統合処理：
    // - 商圏ページ: 4階層（ページ > 枝 > テーブル論理名 + 町名付き > 項目（trade_area_seq昇順 → column_sort昇順））
    // - 通行量ページ: 4階層（ページ > 枝 > テーブル論理名 + traffic_type + traffic_type_seq付き > 項目（traffic_type昇順 → traffic_type_seq有無 → traffic_type_seq昇順 → column_sort昇順））
    // - 競合店ページ: 4階層（ページ > 枝 > テーブル論理名 + 店舗名付き > 項目（column_sort昇順））
    // - 影響度ページ: 4階層（ページ > 枝 > 店舗名キー > 項目（location_impact優先 → location_impact_counterはroute_seq順序））
    //   * 影響度ページの特殊構造：
    //     - location_impact: "影響度\n店舗名" （基本項目）
    //     - location_impact_counter: "影響度\n店舗名\nルート名\n交差点名" （カウンター項目）
    //   * ソート順序: 基本項目 → カウンター項目（route_seq順）
    // - 通常ページ: 3階層（ページ > 枝 > 項目（column_sort昇順））
    // 依存配列: [changeHistory, tableNameMaster, codeMaster] - これらが変更された時のみ再計算
    const groupedData = React.useMemo(() => {
        if (changeHistory.length === 0 || tableNameMaster.length === 0) {
            return {};
        }
        
        return groupChangeHistoryByItemHierarchy(changeHistory, tableNameMaster, codeMaster);
    }, [changeHistory, tableNameMaster, codeMaster]);


    return (
        <Box sx={{ width: "100%" }}>
            <Grid container>
                <Grid item xs={12} sx={{ height: "48px" }} />
            </Grid>
            <Grid container direction={"row"} sx={{ position: "fixed" }}>
                <Grid item sx={{ width: "55px", height: props.height }} />
                <Grid
                    item
                    sx={{ width: props.width, height: props.height, overflowY: "scroll" }}>
                    <Grid item sx={{ marginTop: "10px", marginLeft: "10px", marginBottom: "10px" }}>
                        <Box sx={{ 
                            display: "flex", 
                            alignItems: "center",
                            minHeight: "48px",
                            paddingY: "8px"
                        }}>
                            <Typography sx={{ display: "flex", alignItems: "center" }}>
                                {LOCAL_CONSTANT.LABEL.RFC_ITEM_CHANGE_HISTORY}
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={handleFilterOpen}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {LOCAL_CONSTANT.LABEL.FILTER}
                            </Button>
                            {/* RFCとRRときのみコメント登録ボタンを表示 */}
                            {LOCAL_CONSTANT.USER_TYPE.RFC_RR.includes(userType) && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    onClick={handleCommentOpen}
                                    sx={{ 
                                        margin: "0px 10px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {LOCAL_CONSTANT.LABEL.REGIST_COMMENT}
                                </Button>
                            )}
                            {/* 全展開/全折りたたみボタン */}
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleToggleExpandAll}
                                sx={{ 
                                    margin: "0px 10px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {isAllExpanded ? "全折りたたみ" : "全展開"}
                            </Button>
                        </Box>
                    </Grid>
                    {/* RFC項目変更履歴テーブル */}
                    <TableContainer sx={{ height: "80%", minHeight: "80%", maxHeight: "80%", overflowY: "auto" }}>
                        <Table stickyHeader sx={tableStyle}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderDateStyle}>{LOCAL_CONSTANT.LABEL.DATE}</TableCell>
                                    <TableCell sx={tableHeaderNameStyle}>{LOCAL_CONSTANT.LABEL.EDITOR}</TableCell>
                                    <TableCell sx={tableHeaderSmallStyle}>{LOCAL_CONSTANT.LABEL.BRANCH}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.PAGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.ITEM}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_BEFORE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.AFTER_CHANGE}</TableCell>
                                    <TableCell sx={tableHeaderStyle}>{LOCAL_CONSTANT.LABEL.CHANGE_DETAILS}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(groupedData).map(([pageName, branches]) => {
                                    return Object.entries(branches as any).map(([branchKey, branchContent]) => {    
                                        const branchExpandKey = `${pageName}_${branchKey}`;
                                        const isBranchExpanded = expandedBranches[branchExpandKey];
                                        
                                        // 商圏ページかどうかを判定
                                        const isTradeAreaPage = pageName === "商圏";
                                        // 通行量ページかどうかを判定
                                        const isTrafficPage = pageName === "通行量";
                                        // 競合店の判定を細分化
                                        const isConflictStorePage = pageName === "競合店（CVS）" || pageName === "競合店（SMDS）";  // page_sort="17","18"のみ（4階層）
                                        // 影響度ページかどうかを判定（page_sort === "22"で識別）
                                        // 影響度ページは特殊な4階層構造を持つ：
                                        // - location_impact: 基本項目（店舗名ベース）
                                        // - location_impact_counter: カウンター項目（ルート・交差点ベース）
                                        const isImpactPage = pageName === "影響度";
                        
                                        // 商圏ページ、通行量ページ、競合店ページ、影響度ページは4階層構造
                                        if (isTradeAreaPage || isTrafficPage || isConflictStorePage || isImpactPage) {
                                            // 4階層構造: すべて項目が4階層で表示
                                            const normalItems: {[tableLogicName: string]: {[itemName: string]: any[]}} = branchContent as any;
                                            
                                            // 枝グループヘッダー行用の最新の項目を取得
                                            // 全ての項目（コメント項目も含む）から、publish_timeが最新の項目を取得
                                            let branchFirstItem = null;
                                            
                                            // 全ての項目から日付が最新の項目を取得（コメント項目も含む）
                                            let latestPublishTime = new Date(0); // 最も古い日付で初期化
                                            for (const tableGroup of Object.values(normalItems)) {
                                                for (const itemGroup of Object.values(tableGroup)) {
                                                    if (Array.isArray(itemGroup) && itemGroup.length > 0) {
                                                        // 全ての項目から最新の項目を取得
                                                        for (const item of itemGroup) {
                                                            const itemDate = new Date(item.publish_time);
                                                            if (itemDate > latestPublishTime) {
                                                                latestPublishTime = itemDate;
                                                                branchFirstItem = item;
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            
                                            // 枝レベルのコメントをフィルタリング
                                            const branchNumber = branchKey.replace(/^枝/, ''); // "枝1" → "1"
                                            const branchComments = comment.filter((commentItem: any) => {
                                                return commentItem.target_table === pageName && commentItem.branch === branchNumber;
                                            });
                                            
                                            // 最新のコメントを取得
                                            const latestComment = branchComments.length > 0 
                                                ? branchComments.sort((a, b) => new Date(b.publish_time).getTime() - new Date(a.publish_time).getTime())[0]
                                                : null;
                                                            
                                            return (
                                                <React.Fragment key={`branch-${branchExpandKey}`}>
                                                    {/* 枝グループヘッダー行 */}
                                                    <TableRow>
                                                        <TableCell sx={tableCellDateStyle}>
                                                            {branchFirstItem ? displayTime(branchFirstItem.publish_time) : ""}
                                                        </TableCell>
                                                        <TableCell sx={tableCellNameStyle}>
                                                            {branchFirstItem ? branchFirstItem.update_name : ""}
                                                        </TableCell>
                                                        <TableCell sx={tableCellSmallStyle}>
                                                            {branchFirstItem ? branchFirstItem.branch : ""}
                                                        </TableCell>
                                                        <TableCell 
                                                            sx={{
                                                                ...tableCellWhiteSpace,
                                                                backgroundColor: "#FFFFCC",
                                                                cursor: "pointer"
                                                            }}
                                                            onClick={() => handleBranchGroupClick(pageName, branchKey)}
                                                        >
                                                            {pageName} {isBranchExpanded ? "▲" : "▼"}
                                                        </TableCell>
                                                        <TableCell sx={tableCellWhiteSpace}></TableCell>
                                                        <TableCell sx={tableCellStyle}></TableCell>
                                                        <TableCell sx={tableCellStyle}></TableCell>
                                                        <TableCell sx={tableCellStyle}>
                                                            {latestComment ? latestComment.comment : ""}
                                                        </TableCell>
                                                    </TableRow>
                                                    
                                                    
                                                    {/* 枝展開時: コメント項目は3階層、通常項目は4階層で表示 */}
                                                    {isBranchExpanded && (() => {
                                                        // コメント項目と通常項目を分離
                                                        const commentItems: {[itemName: string]: any[]} = {};
                                                        const regularItems: {[tableLogicName: string]: {[itemName: string]: any[]}} = {};
                                                        
                                                        Object.entries(normalItems).forEach(([tableLogicName, pageItemGroups]) => {
                                                            Object.entries(pageItemGroups).forEach(([itemName, items]) => {
                                                                const firstItem = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                                if (firstItem && firstItem.target_column === "コメント") {
                                                                    // コメント項目：直接詳細レコードとして表示するため分離
                                                                    commentItems[itemName] = items;
                                                                } else {
                                                                    // 通常項目：青い行で表示。展開と折り畳みがある
                                                                    if (!regularItems[tableLogicName]) {
                                                                        regularItems[tableLogicName] = {};
                                                                    }
                                                                    regularItems[tableLogicName][itemName] = items;
                                                                }
                                                            });
                                                        });
                                                        
                                                        return (
                                                            <React.Fragment>
                                                                {/* コメント項目：詳細レコードを直接表示（青い行なし）。展開と折り畳みがない */}
                                                                {Object.entries(commentItems).map(([itemName, items]) => {
                                                                    return (
                                                                        <React.Fragment key={`comment-item-${itemName}`}>
                                                                            {/* コメント項目の詳細レコードを直接表示 */}
                                                                            {Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                    <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                    <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                    <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                    <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                    <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                    <TableCell sx={tableCellStyle}></TableCell>
                                                                                    <TableCell sx={tableCellStyle}></TableCell>
                                                                                    <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                                
                                                                {/* 通常項目：4階層表示（緑の行を含む）。展開と折り畳みがある */}
                                                                {Object.entries(regularItems).map(([tableLogicName, pageItemGroups]) => {
                                                                    const tableExpandKey = `${pageName}_${branchKey}_${tableLogicName}`;
                                                                    const isTableExpanded = expandedTables[tableExpandKey];
                                                                    
                                                                    // テーブル論理名内で日付が最新の項目を取得
                                                                    let tableFirstItem = null;
                                                                    let tableLatestPublishTime = new Date(0);
                                                                    
                                                                    for (const itemGroup of Object.values(pageItemGroups)) {
                                                                        if (Array.isArray(itemGroup) && itemGroup.length > 0) {
                                                                            for (const item of itemGroup) {
                                                                                const itemDate = new Date(item.publish_time);
                                                                                if (itemDate > tableLatestPublishTime) {
                                                                                    tableLatestPublishTime = itemDate;
                                                                                    tableFirstItem = item;
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                    
                                                                    return (
                                                                        <React.Fragment key={`table-expand-${tableExpandKey}`}>
                                                                            {/* テーブル論理名グループヘッダー行 */}
                                                                            <TableRow>
                                                                                <TableCell sx={tableCellDateStyle}>
                                                                                    {tableFirstItem ? displayTime(tableFirstItem.publish_time) : ""}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellNameStyle}>
                                                                                    {tableFirstItem ? tableFirstItem.update_name : ""}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellSmallStyle}>
                                                                                    {tableFirstItem ? tableFirstItem.branch : ""}
                                                                                </TableCell>
                                                                                <TableCell 
                                                                                    sx={{
                                                                                        ...tableCellWhiteSpace,
                                                                                        backgroundColor: "#C0FFC0",
                                                                                        cursor: "pointer"
                                                                                    }}
                                                                                    onClick={() => handleTableExpandGroupClick(pageName, branchKey, tableLogicName)}
                                                                                >
                                                                                    {tableLogicName} {isTableExpanded ? "▲" : "▼"}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellWhiteSpace}></TableCell>
                                                                                <TableCell sx={tableCellStyle}></TableCell>
                                                                                <TableCell sx={tableCellStyle}></TableCell>
                                                                                <TableCell sx={tableCellStyle}></TableCell>
                                                                            </TableRow>
                                                                            
                                                                            {/* テーブル論理名展開時の項目グループ */}
                                                                            {isTableExpanded && Object.entries(pageItemGroups).map(([itemName, items]) => {
                                                                                const itemExpandKey = `${pageName}_${branchKey}_${tableLogicName}_${itemName}`;
                                                                                const isItemExpanded = expandedItems[itemExpandKey];
                                                                                const itemFirstRecord = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                                                
                                                                                // 項目グループの集約データを取得
                                                                                const itemSummary = getItemGroupSummary(items);
                                                                                
                                                                                return (
                                                                                    <React.Fragment key={`item-${itemExpandKey}`}>
                                                                                        {/* 項目グループヘッダー行 */}
                                                                                        <TableRow>
                                                                                            <TableCell sx={tableCellDateStyle}>
                                                                                                {itemFirstRecord ? displayTime(itemFirstRecord.publish_time) : ""}
                                                                                            </TableCell>
                                                                                            <TableCell sx={tableCellNameStyle}>
                                                                                                {itemFirstRecord ? itemFirstRecord.update_name : ""}
                                                                                            </TableCell>
                                                                                            <TableCell sx={tableCellSmallStyle}>
                                                                                                {itemFirstRecord ? itemFirstRecord.branch : ""}
                                                                                            </TableCell>
                                                                                            <TableCell sx={tableCellWhiteSpace}>
                                                                                                {itemFirstRecord ? itemFirstRecord.tableLogicName : ""}
                                                                                            </TableCell>
                                                                                            <TableCell 
                                                                                                sx={{
                                                                                                    ...tableCellWhiteSpace,
                                                                                                    backgroundColor: "#CCFFFF",
                                                                                                    cursor: "pointer"
                                                                                                }}
                                                                                                onClick={() => handleItemGroupClick(pageName, branchKey, itemName, tableLogicName)}
                                                                                            >
                                                                                                {itemName} {isItemExpanded ? "▲" : "▼"}
                                                                                            </TableCell>
                                                                                            <TableCell sx={tableCellStyle}>
                                                                                                {itemSummary ? itemSummary.oldestRecord.beforeValueDisplay : ""}
                                                                                            </TableCell>
                                                                                            <TableCell sx={tableCellStyle}>
                                                                                                {itemSummary ? itemSummary.newestRecord.afterValueDisplay : ""}
                                                                                            </TableCell>
                                                                                            <TableCell sx={tableCellStyle}>
                                                                                                {itemSummary ? change_details(itemSummary.latestRecord.log_type) : ""}
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                        
                                                                                        {/* 項目展開時の詳細レコード */}
                                                                                        {isItemExpanded && Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                            <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                                <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                                <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                                <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                                <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                                <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                                <TableCell sx={tableCellStyle}>{row.beforeValueDisplay}</TableCell>
                                                                                                <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                                <TableCell sx={tableCellStyle}>{change_details(row.log_type)}</TableCell>
                                                                                            </TableRow>
                                                                                        ))}
                                                                                    </React.Fragment>
                                                                                );
                                                                            })}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        );
                                                    })()}
                                                </React.Fragment>
                                            );
                                        } else {
                                            // 3階層構造: ページ > 枝 > 項目
                                            const pageItemGroups = branchContent as {[itemName: string]: any[]};
                                            
                                            // 枝グループヘッダー行用の最新の項目を取得
                                            // 全ての項目（コメント項目も含む）から、publish_timeが最新の項目を取得
                                            let branchFirstItem = null;
                                            
                                            // 全ての項目から日付が最新の項目を取得（コメント項目も含む）
                                            let latestPublishTime = new Date(0); // 最も古い日付で初期化
                                            for (const itemGroup of Object.values(pageItemGroups)) {
                                                if (Array.isArray(itemGroup) && itemGroup.length > 0) {
                                                    // 全ての項目から最新の項目を取得
                                                    for (const item of itemGroup) {
                                                        const itemDate = new Date(item.publish_time);
                                                        if (itemDate > latestPublishTime) {
                                                            latestPublishTime = itemDate;
                                                            branchFirstItem = item;
                                                        }
                                                    }
                                                }
                                            }
                                            
                                            // 枝レベルのコメントをフィルタリング
                                            const branchNumber = branchKey.replace(/^枝/, ''); // "枝1" → "1"
                                            const branchComments = comment.filter((commentItem: any) => {
                                                return commentItem.target_table === pageName && commentItem.branch === branchNumber;
                                            });
                                            
                                            // 最新のコメントを取得
                                            const latestComment = branchComments.length > 0 
                                                ? branchComments.sort((a, b) => new Date(b.publish_time).getTime() - new Date(a.publish_time).getTime())[0]
                                                : null;
                                            
                                            return (
                                                <React.Fragment key={`branch-${branchExpandKey}`}>
                                                    {/* 枝グループヘッダー行 */}
                                                    <TableRow>
                                                        <TableCell sx={tableCellDateStyle}>
                                                            {branchFirstItem ? displayTime(branchFirstItem.publish_time) : ""}
                                                        </TableCell>
                                                        <TableCell sx={tableCellNameStyle}>
                                                            {branchFirstItem ? branchFirstItem.update_name : ""}
                                                        </TableCell>
                                                        <TableCell sx={tableCellSmallStyle}>
                                                            {branchFirstItem ? branchFirstItem.branch : ""}
                                                        </TableCell>
                                                        <TableCell 
                                                            sx={{
                                                                ...tableCellWhiteSpace,
                                                                backgroundColor: "#FFFFCC",
                                                                cursor: "pointer"
                                                            }}
                                                            onClick={() => handleBranchGroupClick(pageName, branchKey)}
                                                        >
                                                            {pageName} {isBranchExpanded ? "▲" : "▼"}
                                                        </TableCell>
                                                        <TableCell sx={tableCellWhiteSpace}></TableCell>
                                                        <TableCell sx={tableCellStyle}></TableCell>
                                                        <TableCell sx={tableCellStyle}></TableCell>
                                                        <TableCell sx={tableCellStyle}>
                                                            {latestComment ? latestComment.comment : ""}
                                                        </TableCell>
                                                    </TableRow>
                                                    
                                                    
                                                    {/* 枝展開時の項目グループ */}
                                                    {isBranchExpanded && (() => {
                                                        // コメント項目と通常項目を分離
                                                        const commentItems: {[itemName: string]: any[]} = {};
                                                        const regularItems: {[itemName: string]: any[]} = {};
                                                        
                                                        Object.entries(pageItemGroups).forEach(([itemName, items]) => {
                                                            const firstItem = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                            if (firstItem && firstItem.target_column === "コメント") {
                                                                // コメント項目：直接詳細レコードとして表示するため分離
                                                                commentItems[itemName] = items;
                                                            } else {
                                                                // 通常項目：青い行で表示
                                                                regularItems[itemName] = items;
                                                            }
                                                        });
                                                        
                                                        return (
                                                            <React.Fragment>
                                                                {/* コメント項目：詳細レコードを直接表示（青い行なし） */}
                                                                {Object.entries(commentItems).map(([itemName, items]) => {
                                                                    return (
                                                                        <React.Fragment key={`comment-item-${itemName}`}>
                                                                            {/* コメント項目の詳細レコードを直接表示 */}
                                                                            {Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                    <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                    <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                    <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                    <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                    <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                    <TableCell sx={tableCellStyle}></TableCell>
                                                                                    <TableCell sx={tableCellStyle}></TableCell>
                                                                                    <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                                
                                                                {/* 通常項目：青い行で展開可能表示。展開と折り畳みがある */}
                                                                {Object.entries(regularItems).map(([itemName, items]) => {
                                                                    const itemExpandKey = `${pageName}_${branchKey}_${itemName}`;
                                                                    const isItemExpanded = expandedItems[itemExpandKey];
                                                                    const itemFirstRecord = Array.isArray(items) && items.length > 0 ? items[0] : null;
                                                                    
                                                                    // 項目グループの集約データを取得
                                                                    const itemSummary = getItemGroupSummary(items);
                                                                    
                                                                    return (
                                                                        <React.Fragment key={`item-${itemExpandKey}`}>
                                                                            {/* 項目グループヘッダー行 */}
                                                                            <TableRow>
                                                                                <TableCell sx={tableCellDateStyle}>
                                                                                    {itemFirstRecord ? displayTime(itemFirstRecord.publish_time) : ""}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellNameStyle}>
                                                                                    {itemFirstRecord ? itemFirstRecord.update_name : ""}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellSmallStyle}>
                                                                                    {itemFirstRecord ? itemFirstRecord.branch : ""}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellWhiteSpace}>
                                                                                    {itemFirstRecord ? itemFirstRecord.tableLogicName : ""}
                                                                                </TableCell>
                                                                                <TableCell 
                                                                                    sx={{
                                                                                        ...tableCellWhiteSpace,
                                                                                        backgroundColor: "#CCFFFF",
                                                                                        cursor: "pointer"
                                                                                    }}
                                                                                    onClick={() => handleItemGroupClick(pageName, branchKey, itemName)}
                                                                                >
                                                                                    {itemName} {isItemExpanded ? "▲" : "▼"}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellStyle}>
                                                                                    {itemSummary ? itemSummary.oldestRecord.beforeValueDisplay : ""}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellStyle}>
                                                                                    {itemSummary ? itemSummary.newestRecord.afterValueDisplay : ""}
                                                                                </TableCell>
                                                                                <TableCell sx={tableCellStyle}>
                                                                                    {itemSummary ? change_details(itemSummary.latestRecord.log_type) : ""}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                            
                                                                            {/* 項目展開時の詳細レコード */}
                                                                            {isItemExpanded && Array.isArray(items) && items.map((row: any, index: number) => (
                                                                                <TableRow key={`${row.primaryKey}-time-${row.publish_time}-${index}`}>
                                                                                    <TableCell sx={tableCellDateStyle}>{displayTime(row.publish_time)}</TableCell>
                                                                                    <TableCell sx={tableCellNameStyle}>{row.update_name}</TableCell>
                                                                                    <TableCell sx={tableCellSmallStyle}>{row.branch}</TableCell>
                                                                                    <TableCell sx={tableCellWhiteSpace}>{row.tableLogicName}</TableCell>
                                                                                    <TableCell sx={tableCellWhiteSpace}>{row.columnLogicName}</TableCell>
                                                                                    <TableCell sx={tableCellStyle}>{row.beforeValueDisplay}</TableCell>
                                                                                    <TableCell sx={tableCellStyle}>{row.afterValueDisplay}</TableCell>
                                                                                    <TableCell sx={tableCellStyle}>{change_details(row.log_type)}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        );
                                                    })()}
                                                </React.Fragment>
                                            );
                                        }
                                    });
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
            <ChangeHistoryTableComment
                props={{
                    open: commentOpen,
                    params: {
                        planAreaId: planAreaId,
                        branch: latestBranch,
                        comment: "",
                    },
                    pages: filterOptions.pages,
                    handleModal: setCommentOpen,
                    handleSaveComment: handleSaveComment,
                    handleCanCelComment: handleCanCelComment,
                }}
            />
            <FilterModal
                open={filterModalOpen}
                onClose={handleFilterClose}
                onApplyFilter={handleApplyFilter}
                filterOptions={filterOptions}
                currentFilters={filterCriteria}
            />
        </Box>
    );
};

export default ItemChangeHistory;
